import { useTranslation } from "@privacy/translations";
import type { GridColDef } from "@mui/x-data-grid-premium";
import Typography from "@mui/material/Typography";
import type { IconProps } from "../../../shared/icons";
import {
  DpiaIcon3,
  RegistrationControllerIcon3,
  SystemIcon3,
  PersonIcon2,
  PermissionIcon3,
} from "../../../shared/icons";
import type { GridViewUser } from "../../../api/users";
import { prefetchUsersGridView, useGetUsersGridView } from "../../../api/users";
import { usePrivacyDocumentTitle } from "../../../shared/usePrivacyDocumentTitle";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { DynamicGrid } from "../../../dynamicGrid";
import { EmptyState } from "../../../shared/EmptyState";
import { multilineEllipsis } from "../../../shared/multilineEllipsis";
import { UserGridToolbar } from "./UserGridToolbar";
import { useScimEditingMode } from "../shared/useScimEditingMode";
import { RESPONSIBLE_ENTITIES_PATHS, type ResponsibleEntitiesKey } from "./constants";
import { UserActionMenu, UserDrawer } from "./UserDrawer/UserDrawer";

const UserGridPage = () => {
  usePrivacyDocumentTitle("Sidebar.People");
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const userGridViewQuery = useGetUsersGridView();
  const { isScimEnabled } = useScimEditingMode();

  return (
    <DynamicGrid.Grid
      rowsData={userGridViewQuery.data}
      form="no-form"
      getRowId={getRowId}
      mainColumnProps={{
        headerName: t("User.Grid.NameHeader"),
        renderLabel: ({ firstName, lastName }) => `${firstName} ${lastName}`,
        renderIcon: ({ iconProps }) => <PersonIcon2 {...iconProps} />,
      }}
      getColumns={() => [
        emailColumn({ headerName: t("User.Grid.EmailHeader") }),
        responsibledEntitesColumn({
          headerName: t("User.Grid.RegistrationsWhereResponsibleHeader"),
          field: "registrationsWhereResponsible",
          path: RESPONSIBLE_ENTITIES_PATHS.registrationsWhereResponsible,
          Icon: RegistrationControllerIcon3,
        }),
        responsibledEntitesColumn({
          headerName: t("User.Grid.ProcessorRegistrationsWhereResponsibleHeader"),
          field: "processorRegistrationsWhereResponsible",
          path: RESPONSIBLE_ENTITIES_PATHS.processorRegistrationsWhereResponsible,
          Icon: RegistrationControllerIcon3,
        }),
        responsibledEntitesColumn({
          headerName: t("User.Grid.DPIAsWhereResponsibleHeader"),
          field: "dpiasWhereResponsible",
          path: RESPONSIBLE_ENTITIES_PATHS.dpiasWhereResponsible,
          Icon: DpiaIcon3,
        }),
        responsibledEntitesColumn({
          headerName: t("User.Grid.SystemsWhereResponsibleHeader"),
          field: "systemsWhereResponsible",
          path: RESPONSIBLE_ENTITIES_PATHS.systemsWhereResponsible,
          Icon: SystemIcon3,
        }),
        rolesColumn({ headerName: t("User.Grid.Drawer.Permissions.Label") }),
      ]}
      onRowSelect={id => navigate(paths.userById(id))}
      Toolbar={UserGridToolbar}
      Footer={UserGridFooter}
      NoRowsOverlay={NoUserOverlay}
      texts={{ searchFieldPlaceholder: t("User.Grid.SearchFieldLabel") }}
      partsSx={{ mainSx: { mr: "revert" } }}
      renderActionMenu={
        isScimEnabled
          ? undefined
          : ({ row }) => <UserActionMenu user={row} onDeleted={() => navigate(paths.usersGrid())} />
      }
    />
  );
};

const getRowId = (row: GridViewUser) => row.userId;

const emailColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewUser> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: "email" }),
  width: 280,
  valueGetter: (_, row) => row.email,
  renderCell: ({ row }) => {
    if (!row.email) return null;

    return (
      <Typography variant="body2" sx={multilineEllipsis(2)}>
        {row.email}
      </Typography>
    );
  },
});

const responsibledEntitesColumn = ({
  headerName,
  field,
  Icon,
  path,
}: {
  headerName: string;
  field: ResponsibleEntitiesKey;
  Icon: React.ComponentType<IconProps>;
  path: string;
}): GridColDef<GridViewUser> => ({
  ...DynamicGrid.requiredColDef({ headerName, field }),
  valueGetter: (_, row) => row[field].length,
  width: 45,

  renderCell: ({ row }) => {
    return <ResponsibledEntitesCell entities={row[field]} entityPath={path} Icon={Icon} userId={row.userId} />;
  },
});

type ResponsibledEntitesCellProps = {
  entities: Array<{ id: string; name: string }>;
  entityPath: string;
  Icon: React.ComponentType<IconProps>;
  userId: string;
};

const ResponsibledEntitesCell = ({ entities, entityPath, Icon, userId }: ResponsibledEntitesCellProps) => {
  const count = entities.length;
  const { paths } = usePrivacyNavigate();

  return <DynamicGrid.OpenSubDrawerLink Icon={Icon} label={count} to={`${paths.userById(userId)}/${entityPath}`} />;
};

const UserGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="User.Grid.NumberOfRows" />;

const NoUserOverlay = () => {
  return <EmptyState labelKey="User.Grid.NoRows" />;
};

const rolesColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewUser> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: "roles" }),
  width: 45,
  renderCell: ({ row }) => <RoleCell userId={row.userId} />,
});

const RoleCell = ({ userId }: { userId: string }) => {
  const { paths } = usePrivacyNavigate();

  return <DynamicGrid.OpenSubDrawerLink Icon={PermissionIcon3} to={`${paths.userById(userId)}/roles`} />;
};

export const userGridPageRoutes = DynamicGrid.setupRoutes({
  gridPrefetcher: prefetchUsersGridView,
  gridElement: <UserGridPage />,
  rowDrawerRoutes: [{ index: true, path: "*", element: <UserDrawer /> }],
});
